import React, { useState, useEffect, useRef } from 'react';
import { HiX } from 'react-icons/hi';
import { HiPaperAirplane } from 'react-icons/hi2';
import { fetchListComments, addListComment, deleteListComment, ListComment } from '../../services/listService';
import { useAuthStore } from '../../stores';
import ThemeLoader from '../../components/ThemeLoader/ThemeLoader';
import toast from 'react-hot-toast';

interface StoreInfo {
  id: string;
  site: string;
  address?: string;
}

interface Props {
  listName: string;
  stores: StoreInfo[];
  onClose: () => void;
  onCountChange?: (count: number) => void;
  initialStoreId?: string | null;
  readOnly?: boolean;
}

const AVATAR_COLORS = [
  '#003da6', '#059669', '#d97706', '#7c3aed', '#dc2626',
  '#0891b2', '#be185d', '#4f46e5', '#15803d', '#b45309',
];

function getAvatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatTime(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) {
    return `Yesterday at ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getInitials(user: { name: string | null; email: string }): string {
  if (user.name) {
    const parts = user.name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }
  return user.email.charAt(0).toUpperCase();
}

function getUserDisplayName(user: { name: string | null; email: string }): string {
  return user.name || user.email.split('@')[0];
}

export default function ListComments({ listName, stores, onClose, onCountChange, initialStoreId, readOnly }: Props) {
  const [comments, setComments] = useState<ListComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(initialStoreId || null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const currentUserId = (useAuthStore.getState().user as any)?.id;
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const loadComments = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    const data = await fetchListComments(listName);
    setComments(data);
    if (showLoader) setLoading(false);
    let total = 0;
    data.forEach((c: any) => { total++; total += (c.replies?.length || 0); });
    onCountChangeRef.current?.(total);
  };

  useEffect(() => {
    loadComments(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listName]);

  // Auto-scroll to bottom when comments load or new comment added
  useEffect(() => {
    if (!loading && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading, comments]);

  // Show all top-level comments (no filtering — store chip only controls what new messages attach to)
  const displayComments = comments;

  const totalCount = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  const selectedStore = selectedStoreId ? stores.find(s => s.id === selectedStoreId) : null;

  // --- Handlers ---

  const handleSendMessage = async () => {
    const text = messageInput.trim();
    if (!text) return;
    const result = await addListComment(listName, text, selectedStoreId || null);
    if (result.success) {
      setMessageInput('');
      await loadComments();
    } else {
      toast.error(result.error || 'Failed to post comment');
    }
  };

  const handleReply = async (parentId: string) => {
    const text = replyText.trim();
    if (!text) return;
    const parent = comments.find(c => c.id === parentId);
    const result = await addListComment(listName, text, parent?.poi_id || null, parentId);
    if (result.success) {
      setReplyingTo(null);
      setReplyText('');
      await loadComments();
    } else {
      toast.error(result.error || 'Failed to post reply');
    }
  };

  const handleDelete = async (commentId: string) => {
    const result = await deleteListComment(commentId);
    if (result.success) {
      await loadComments();
    } else {
      toast.error(result.error || 'Failed to delete comment');
    }
  };

  // --- Render helpers ---

  const renderComment = (comment: ListComment, isReply = false) => (
    <div className="lsc-comment" key={comment.id}>
      <div className="lsc-comment-row">
        <div className="lsc-avatar" style={{ background: getAvatarColor(comment.user_id) }}>
          {getInitials(comment.user)}
        </div>
        <div className="lsc-comment-content">
          <div className="lsc-comment-header">
            <span className="lsc-comment-name">{getUserDisplayName(comment.user)}</span>
            <span className="lsc-comment-dot">·</span>
            <span className="lsc-comment-time">{formatTime(comment.created_at)}</span>
          </div>
          {/* Show store name tag on store-specific comments */}
          {comment.poi_id && (
            <span className="lsc-comment-store-tag">
              {stores.find(s => s.id === comment.poi_id)?.site || comment.poi_id}
            </span>
          )}
          <p className="lsc-comment-text">{comment.comment}</p>
          {!readOnly && (
            <div className="lsc-comment-actions">
              {!isReply && (
                <button
                  className="lsc-action-btn"
                  onClick={() => { setReplyingTo(replyingTo === comment.id ? null : comment.id); setReplyText(''); }}
                >
                  Reply
                </button>
              )}
              {comment.user_id === currentUserId && (
                <button className="lsc-action-btn lsc-action-delete" onClick={() => handleDelete(comment.id)}>
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {!isReply && comment.replies && comment.replies.length > 0 && (
        <div className="lsc-replies">
          {comment.replies.map((reply: any) => renderComment(reply, true))}
        </div>
      )}

      {/* Inline reply input */}
      {!readOnly && !isReply && replyingTo === comment.id && (
        <div className="lsc-reply-input-row">
          <div className="lsc-avatar lsc-avatar-sm" style={{ background: currentUserId ? getAvatarColor(currentUserId) : '#9ca3af' }}>
            {currentUserId ? getInitials((useAuthStore.getState().user as any) || { email: '' }) : '?'}
          </div>
          <input
            className="lsc-inline-input"
            placeholder="Write a reply..."
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(comment.id); }
              if (e.key === 'Escape') { setReplyingTo(null); setReplyText(''); }
            }}
            autoFocus
          />
          <button
            className="lsc-send-btn lsc-send-sm"
            onClick={() => handleReply(comment.id)}
            disabled={!replyText.trim()}
          >
            <HiPaperAirplane size={12} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="ls-comments-backdrop" onClick={onClose} />
      <div className="ls-comments-panel">
        {/* Header */}
        <div className="lsc-panel-header">
          <div>
            <h3 className="lsc-panel-title">
              {totalCount} comment{totalCount !== 1 ? 's' : ''}
              <span className="lsc-panel-title-dot">·</span>
              <span className="lsc-panel-title-name">{listName}</span>
            </h3>
          </div>
          <button className="ls-comments-close" onClick={onClose}><HiX size={18} /></button>
        </div>

        {/* Scrollable message area */}
        <div className="lsc-panel-body lsc-chat-body" ref={scrollRef}>
          {loading ? (
            <ThemeLoader text="Loading comments" />
          ) : displayComments.length === 0 ? (
            <div className="ls-comments-empty">
              <p>No comments yet. Start a conversation.</p>
            </div>
          ) : (
            displayComments.map(c => renderComment(c))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Store chip + Chat input at bottom (hidden in read-only mode) */}
        {!readOnly && (
          <>
            {selectedStore && (
              <div className="lsc-store-chip-bar">
                <div className="lsc-store-chip">
                  <span className="lsc-store-chip-name">{selectedStore.site}</span>
                  {selectedStore.address && (
                    <span className="lsc-store-chip-addr">{selectedStore.address}</span>
                  )}
                  <button className="lsc-store-chip-x" onClick={() => setSelectedStoreId(null)}>
                    <HiX size={12} />
                  </button>
                </div>
              </div>
            )}
            <div className="lsc-chat-input-bar">
              <div className="lsc-avatar lsc-avatar-sm" style={{ background: currentUserId ? getAvatarColor(currentUserId) : '#9ca3af' }}>
                {currentUserId ? getInitials((useAuthStore.getState().user as any) || { email: '' }) : '?'}
              </div>
              <input
                className="lsc-inline-input"
                placeholder={selectedStoreId ? 'Comment on this store...' : 'Add a comment...'}
                value={messageInput}
                onChange={e => setMessageInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              />
              <button className="lsc-send-btn lsc-send-sm" onClick={handleSendMessage} disabled={!messageInput.trim()}>
                <HiPaperAirplane size={12} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
